import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const PromoCodes = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [sortBy, setSortBy] = useState('user_count');
  const [searchQuery, setSearchQuery] = useState('');
  const [trendData, setTrendData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [period, setPeriod] = useState('30days');
  const [granularity, setGranularity] = useState('daily');
  const [rcStatus, setRcStatus] = useState(null);
  const [syncRun, setSyncRun] = useState(null);
  const [syncMessage, setSyncMessage] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [detectResult, setDetectResult] = useState(null);
  const [estimatePrice, setEstimatePrice] = useState(19);
  const [estimateDeduction, setEstimateDeduction] = useState(15);
  const [metaDraft, setMetaDraft] = useState({}); // { [PROMO]: { blogger_name, cost_paid_amount, cost_paid_currency } }
  const [metaSaving, setMetaSaving] = useState({}); // { [PROMO]: 'saving' | 'saved' | 'error' }
  const [projectionDraft, setProjectionDraft] = useState({});
  const [rates, setRates] = useState(null); // { rates: { USD:1, AMD:..., RUB:... }, supported: [...] }

  const isSyncRunning = syncRun?.status === 'running';
  const { fetchWithAuth } = useAuth();

  useEffect(() => {
    fetchData();
    fetchRcStatus();
    fetchRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRates = async () => {
    try {
      const response = await fetchWithAuth('/api/admin/exchange-rates');
      if (!response.ok) return;
      const result = await response.json();
      setRates(result);
    } catch (err) {
      console.error('Error fetching exchange rates:', err);
    }
  };

  const convertToUsd = (amount, currency) => {
    if (amount === null || amount === undefined || amount === '') return null;
    const num = Number(amount);
    if (!Number.isFinite(num)) return null;
    if (!currency || currency === 'USD') return num;
    const rate = rates?.rates?.[currency];
    if (!rate || rate <= 0) return null;
    return num / rate;
  };

  const fetchRcStatus = async () => {
    try {
      const response = await fetchWithAuth('/api/admin/revenuecat/status');
      if (!response.ok) return;
      const result = await response.json();
      setRcStatus(result);
      // Also seed syncRun with the last known run so the panel renders state on first load.
      if (result.lastRun) setSyncRun(result.lastRun);
    } catch (err) {
      console.error('Error fetching RC status:', err);
    }
  };

  const fetchSyncProgress = async (runId) => {
    try {
      const response = await fetchWithAuth(
        `/api/admin/revenuecat/sync/progress${runId ? `?runId=${runId}` : ''}`
      );
      if (!response.ok) return null;
      const result = await response.json();
      return result.run;
    } catch (err) {
      console.error('Error fetching sync progress:', err);
      return null;
    }
  };

  // Poll progress while a sync is running
  useEffect(() => {
    if (!isSyncRunning) return undefined;
    const runId = syncRun?.id;
    let cancelled = false;
    const tick = async () => {
      const run = await fetchSyncProgress(runId);
      if (cancelled) return;
      if (run) setSyncRun(run);
      if (run && run.status === 'success') {
        // Refresh promo data once sync finishes
        fetchData();
        fetchRcStatus();
      }
    };
    const interval = setInterval(tick, 1500);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSyncRunning, syncRun?.id]);

  const handleDetect = async () => {
    if (detecting) return;
    setDetecting(true);
    setDetectResult(null);
    try {
      const response = await fetchWithAuth('/api/admin/revenuecat/detect-mapping', {
        method: 'POST'
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Detect failed');
      setDetectResult(result);
    } catch (err) {
      setDetectResult({ error: err.message });
    } finally {
      setDetecting(false);
    }
  };

  const handleSync = async (scope = 'promo_users') => {
    if (isSyncRunning) return;
    setSyncMessage(null);
    try {
      const response = await fetchWithAuth('/api/admin/revenuecat/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scope })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Sync failed to start');
      // Immediately seed a "running" run so the UI shows progress state on next poll.
      const seed = await fetchSyncProgress(result.runId);
      setSyncRun(seed || { id: result.runId, status: 'running', scope, progress_current: 0, progress_total: 0 });
      if (result.alreadyRunning) {
        setSyncMessage({ type: 'info', text: `A sync is already running (run #${result.runId}). Polling progress.` });
      }
    } catch (err) {
      setSyncMessage({ type: 'error', text: err.message });
    }
  };

  // Inclusive month count between two YYYY-MM(-DD) strings.
  // monthsBetween('2025-09-01', '2025-12-15') === 4 (Sep, Oct, Nov, Dec).
  const monthsBetween = (startStr, endStr) => {
    if (!startStr || !endStr) return 0;
    const [y1, m1] = String(startStr).slice(0, 7).split('-').map(Number);
    const [y2, m2] = String(endStr).slice(0, 7).split('-').map(Number);
    if (!y1 || !m1 || !y2 || !m2) return 0;
    const diff = (y2 - y1) * 12 + (m2 - m1) + 1;
    return diff > 0 ? diff : 0;
  };

  const toMonthInput = (dateStr) => (dateStr ? String(dateStr).slice(0, 7) : '');
  const fromMonthInput = (ym) => (ym ? `${ym}-01` : null);

  const effectivePeriodCount = (paymentPeriod) => {
    if (paymentPeriod.unit === 'month' && paymentPeriod.period_start && paymentPeriod.period_end) {
      const computed = monthsBetween(paymentPeriod.period_start, paymentPeriod.period_end);
      if (computed > 0) return computed;
    }
    const typed = Number(paymentPeriod.count) || 1;
    return Math.max(1, Math.floor(typed));
  };

  const computeRevenueNet = (promo) => {
    const real = Number(promo.revenue_proceeds_usd || 0);
    if (real > 0) return { value: real, source: 'real' };
    const netSubs = Math.max(0, (promo.subscribers || 0) - (promo.refunded_users || 0));
    const est = netSubs * estimatePrice * (1 - estimateDeduction / 100);
    return { value: est, source: 'est' };
  };

  const getDraft = (promo, field) => {
    const draft = metaDraft[promo.promo_code];
    if (draft && draft[field] !== undefined) return draft[field];
    if (field === 'blogger_name') return promo.blogger_name || '';
    if (field === 'cost_paid_amount') return promo.cost_paid_amount ?? '';
    if (field === 'cost_paid_currency') return promo.cost_paid_currency || 'USD';
    if (field === 'payment_count') return promo.payment_count ?? 1;
    if (field === 'payment_unit') return promo.payment_unit || 'post';
    if (field === 'payment_period_start') return promo.payment_period_start || '';
    if (field === 'payment_period_end') return promo.payment_period_end || '';
    return '';
  };

  const getCostPeriods = (promo) => {
    const drafted = metaDraft[promo.promo_code]?.cost_periods;
    if (Array.isArray(drafted)) return drafted;
    if (Array.isArray(promo.cost_periods) && promo.cost_periods.length > 0) {
      return promo.cost_periods;
    }
    if (promo.cost_paid_amount !== null && promo.cost_paid_amount !== undefined) {
      return [{
        amount: promo.cost_paid_amount,
        currency: promo.cost_paid_currency || 'USD',
        count: promo.payment_count || 1,
        unit: promo.payment_unit || 'post',
        period_start: promo.payment_period_start || null,
        period_end: promo.payment_period_end || null,
        usd_total: promo.cost_paid_usd ?? null
      }];
    }
    return [];
  };

  const setDraft = (promoCode, field, value) => {
    setMetaDraft(prev => ({
      ...prev,
      [promoCode]: { ...(prev[promoCode] || {}), [field]: value }
    }));
  };

  const setCostPeriodDraft = (promo, periodIndex, field, value) => {
    const periods = getCostPeriods(promo).map((period, index) => (
      index === periodIndex
        ? {
            ...period,
            [field]: value,
            ...(['amount', 'currency', 'count', 'unit', 'period_start', 'period_end'].includes(field)
              ? { rate_at_save: null, usd_total: null }
              : {})
          }
        : period
    ));
    setDraft(promo.promo_code, 'cost_periods', periods);
  };

  const addCostPeriod = (promo) => {
    const periods = getCostPeriods(promo);
    const previous = periods[periods.length - 1];
    setDraft(promo.promo_code, 'cost_periods', [
      ...periods,
      {
        amount: '',
        currency: previous?.currency || 'USD',
        count: 1,
        unit: previous?.unit || 'post',
        period_start: null,
        period_end: null
      }
    ]);
  };

  const setProjection = (promoCode, field, value) => {
    setProjectionDraft(prev => ({
      ...prev,
      [promoCode]: { ...(prev[promoCode] || {}), [field]: value }
    }));
  };

  const saveMeta = async (promoCode, periodsOverride) => {
    const draft = metaDraft[promoCode];
    if (!draft && periodsOverride === undefined) return;
    const promoData = data?.promoCodes?.find(p => p.promo_code === promoCode);
    const pickDraft = (field, fallback) =>
      draft?.[field] !== undefined ? draft[field] : fallback;

    const blogger = pickDraft('blogger_name', promoData?.blogger_name || null);
    const rawPeriods = periodsOverride !== undefined
      ? periodsOverride
      : pickDraft('cost_periods', getCostPeriods(promoData));
    // A freshly-added blank section remains local until its amount is entered.
    if (rawPeriods.some(period => period.amount === '' || period.amount === null || period.amount === undefined)) return;
    const costPeriods = rawPeriods.map(period => ({
      amount: Number(period.amount),
      currency: period.currency || 'USD',
      count: effectivePeriodCount(period),
      unit: period.unit === 'month' ? 'month' : 'post',
      period_start: period.period_start || null,
      period_end: period.period_end || null,
      rate_at_save: period.rate_at_save ?? null,
      usd_total: period.usd_total ?? null
    }));

    setMetaSaving(prev => ({ ...prev, [promoCode]: 'saving' }));
    try {
      const response = await fetchWithAuth(
        `/api/analytics/promo-codes/${encodeURIComponent(promoCode)}/meta`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            blogger_name: blogger || null,
            cost_periods: costPeriods
          })
        }
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Save failed');
      setData(prev => prev && {
        ...prev,
        promoCodes: prev.promoCodes.map(p =>
          p.promo_code === promoCode
            ? {
                ...p,
                blogger_name: blogger || null,
                cost_paid_amount: result.cost_paid_amount ?? null,
                cost_paid_currency: result.cost_paid_currency || 'USD',
                cost_paid_rate_at_save: result.cost_paid_rate_at_save ?? null,
                cost_paid_usd: result.cost_paid_usd ?? null,
                payment_count: result.payment_count ?? 1,
                payment_unit: result.payment_unit || 'post',
                payment_period_start: result.payment_period_start || null,
                payment_period_end: result.payment_period_end || null,
                cost_periods: result.cost_periods || []
              }
            : p
        )
      });
      setMetaDraft(prev => {
        const next = { ...prev };
        delete next[promoCode];
        return next;
      });
      setMetaSaving(prev => ({ ...prev, [promoCode]: 'saved' }));
      setTimeout(() => {
        setMetaSaving(prev => {
          const next = { ...prev };
          delete next[promoCode];
          return next;
        });
      }, 1500);
    } catch (err) {
      setMetaSaving(prev => ({ ...prev, [promoCode]: 'error' }));
      console.error('Save meta failed:', err);
    }
  };

  const removeCostPeriod = (promo, periodIndex) => {
    const periods = getCostPeriods(promo).filter((_, index) => index !== periodIndex);
    setDraft(promo.promo_code, 'cost_periods', periods);
    saveMeta(promo.promo_code, periods);
  };

  const handleStop = async () => {
    if (!syncRun?.id) return;
    try {
      const response = await fetchWithAuth('/api/admin/revenuecat/sync/stop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ runId: syncRun.id })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Stop failed');
      setSyncMessage({
        type: 'info',
        text: result.stopped
          ? `Cancelling run #${result.runId}… (will stop after current customer)`
          : `No active sync to stop in this server process.`
      });
    } catch (err) {
      setSyncMessage({ type: 'error', text: err.message });
    }
  };

  // Fetch trend data when promo code, period, or granularity changes
  useEffect(() => {
    if (selectedPromo) {
      fetchTrendData(selectedPromo);
    } else {
      setTrendData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPromo, period, granularity]);

  const fetchData = async () => {
    try {
      const response = await fetchWithAuth('/api/analytics/promo-codes');
      if (!response.ok) throw new Error('Failed to fetch promo code data');
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrendData = async (promoCode) => {
    setTrendLoading(true);
    try {
      const response = await fetchWithAuth(
        `/api/analytics/promo-codes/${encodeURIComponent(promoCode)}/trends?period=${period}&granularity=${granularity}`
      );
      if (!response.ok) throw new Error('Failed to fetch trend data');
      const result = await response.json();
      setTrendData(result);
    } catch (err) {
      console.error('Error fetching trend data:', err);
    } finally {
      setTrendLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading promo code analytics...</div>;
  if (error) return <div className="error">Error: {error}</div>;
  if (!data) return null;

  const { promoCodes, topUsersByPromo, summary } = data;

  // Filter and sort promo codes
  const getFilteredPromoCodes = () => {
    let filtered = [...promoCodes];
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toUpperCase();
      filtered = filtered.filter(p => 
        p.promo_code.toUpperCase().includes(query)
      );
    }
    
    // Sort
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'user_count':
          return b.user_count - a.user_count;
        case 'engagement':
          return b.median_food_logs - a.median_food_logs;
        case 'food_logs':
          return b.total_food_logs - a.total_food_logs;
        case 'recent':
          return new Date(b.last_user_date) - new Date(a.last_user_date);
        case 'subscribers':
          return (b.subscribers || 0) - (a.subscribers || 0);
        case 'conversion':
          return (b.conversion_rate || 0) - (a.conversion_rate || 0);
        case 'churn':
          return (b.churn_rate || 0) - (a.churn_rate || 0);
        case 'revenue': {
          const ra = Number(a.revenue_proceeds_usd) > 0
            ? Number(a.revenue_proceeds_usd)
            : Math.max(0, (a.subscribers || 0) - (a.refunded_users || 0)) * estimatePrice * (1 - estimateDeduction / 100);
          const rb = Number(b.revenue_proceeds_usd) > 0
            ? Number(b.revenue_proceeds_usd)
            : Math.max(0, (b.subscribers || 0) - (b.refunded_users || 0)) * estimatePrice * (1 - estimateDeduction / 100);
          return rb - ra;
        }
        case 'refunds':
          return (b.refunded_users || 0) - (a.refunded_users || 0);
        case 'profit': {
          const ra = computeRevenueNet(a).value - Number(a.cost_paid_usd || 0);
          const rb = computeRevenueNet(b).value - Number(b.cost_paid_usd || 0);
          return rb - ra;
        }
        case 'roi': {
          const ca = Number(a.cost_paid_usd || 0);
          const cb = Number(b.cost_paid_usd || 0);
          const ra = ca > 0 ? (computeRevenueNet(a).value - ca) / ca : -Infinity;
          const rb = cb > 0 ? (computeRevenueNet(b).value - cb) / cb : -Infinity;
          return rb - ra;
        }
        default:
          return b.user_count - a.user_count;
      }
    });
  };
  
  const filteredPromoCodes = getFilteredPromoCodes();

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getEngagementColor = (median) => {
    if (median >= 50) return '#10b981';
    if (median >= 20) return '#3b82f6';
    if (median >= 5) return '#f59e0b';
    return '#ef4444';
  };

  return (
    <div>
      <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '8px', color: '#1a1a1a' }}>
        🎟️ Promo Code Analytics
      </h1>
      <p style={{ color: '#666', marginBottom: '16px', fontSize: '16px' }}>
        Track users by promo codes and measure engagement
      </p>

      {/* RevenueCat sync controls */}
      <div className="card" style={{
        marginBottom: '24px',
        background: rcStatus?.configured ? '#f0fdf4' : '#fef3c7',
        border: `1px solid ${rcStatus?.configured ? '#bbf7d0' : '#fde68a'}`
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
        <div style={{ flex: 1, minWidth: '300px' }}>
          <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
            💳 RevenueCat Subscription Sync
          </div>
          {rcStatus?.configured ? (
            <div style={{ fontSize: '12px', color: '#666' }}>
              Mapped via <code>users.{rcStatus.userIdField}</code> → app_user_id.
              {rcStatus.counts && (
                <> Cached: <strong>{rcStatus.counts.total_subs}</strong> subs ({rcStatus.counts.active_subs} active, {rcStatus.counts.unique_users} users).</>
              )}
              {!isSyncRunning && syncRun?.finished_at && (
                <> Last sync: {new Date(syncRun.finished_at).toLocaleString()} — <strong>{syncRun.status}</strong>.</>
              )}
            </div>
          ) : (
            <div style={{ fontSize: '12px', color: '#92400e' }}>
              Not configured. Set <code>REVENUECAT_API_KEY</code> and <code>REVENUECAT_PROJECT_ID</code> in <code>.env</code>.
            </div>
          )}

          {/* Live progress while a sync is running (or on completion) */}
          {syncRun && (isSyncRunning || ['cancelled', 'error', 'success'].includes(syncRun.status)) && (() => {
            const total = Number(syncRun.progress_total) || 0;
            const cur = Number(syncRun.progress_current) || 0;
            const pct = total > 0 ? Math.min(100, Math.round((cur / total) * 100)) : 0;
            const startedMs = syncRun.started_at ? new Date(syncRun.started_at).getTime() : null;
            const elapsedSec = startedMs ? Math.max(1, (Date.now() - startedMs) / 1000) : null;
            const rate = elapsedSec && cur > 0 ? cur / elapsedSec : 0;
            const remaining = rate > 0 && total > cur ? Math.round((total - cur) / rate) : null;
            const fmtEta = (s) => {
              if (s === null) return '—';
              if (s < 60) return `${s}s`;
              const m = Math.floor(s / 60);
              const r = s % 60;
              return `${m}m ${r}s`;
            };
            const barColor =
              syncRun.status === 'error' ? '#ef4444' :
              syncRun.status === 'cancelled' ? '#f59e0b' :
              syncRun.status === 'success' ? '#10b981' : '#7c3aed';
            return (
              <div style={{
                marginTop: '10px',
                padding: '10px 12px',
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '6px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
                  <span>
                    <strong>Run #{syncRun.id}</strong> · scope: <code>{syncRun.scope}</code> · status:{' '}
                    <span style={{ color: barColor, fontWeight: 600 }}>{syncRun.status}</span>
                  </span>
                  <span style={{ color: '#666' }}>
                    {cur.toLocaleString()} / {total.toLocaleString()} customers ({pct}%)
                    {isSyncRunning && remaining !== null && <> · ETA {fmtEta(remaining)}</>}
                  </span>
                </div>
                <div style={{ height: '8px', background: '#f3f4f6', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    width: `${pct}%`,
                    background: barColor,
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '6px', fontSize: '11px', color: '#666' }}>
                  <span>{syncRun.customers_with_subs || 0} with subs</span>
                  <span>{syncRun.subscriptions_fetched || 0} subs fetched</span>
                  <span>{syncRun.subscriptions_upserted || 0} upserted</span>
                  <span><strong style={{ color: '#10b981' }}>{syncRun.users_matched || 0}</strong> matched to users</span>
                  {syncRun.error_message && (
                    <span style={{ color: '#dc2626' }}>error: {syncRun.error_message}</span>
                  )}
                </div>
              </div>
            );
          })()}

          {syncMessage && (
            <div style={{
              marginTop: '6px',
              fontSize: '12px',
              color:
                syncMessage.type === 'error' ? '#dc2626' :
                syncMessage.type === 'info' ? '#0369a1' : '#15803d'
            }}>
              {syncMessage.text}
            </div>
          )}
          {detectResult && (
            <div style={{
              marginTop: '8px',
              padding: '8px 10px',
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              fontSize: '12px'
            }}>
              {detectResult.error ? (
                <span style={{ color: '#dc2626' }}>Error: {detectResult.error}</span>
              ) : (
                <>
                  <div style={{ marginBottom: '4px' }}>
                    <strong>Recommended:</strong>{' '}
                    {detectResult.recommended ? (
                      <code style={{ background: '#dcfce7', padding: '2px 6px', borderRadius: '4px', color: '#166534' }}>
                        REVENUECAT_USER_ID_FIELD={detectResult.recommended}
                      </code>
                    ) : (
                      <span style={{ color: '#dc2626' }}>none — no probes hit. Check API key permissions or whether iOS uses a custom UUID.</span>
                    )}
                    {' '}(current: <code>{detectResult.currentField}</code>)
                  </div>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', color: '#666' }}>
                    {Object.entries(detectResult.results).map(([field, r]) => (
                      <span key={field}>
                        <code>{field}</code>: {r.hits}/{r.probed}
                      </span>
                    ))}
                  </div>
                  {detectResult.recommended && detectResult.recommended !== detectResult.currentField && (
                    <div style={{ marginTop: '6px', color: '#92400e' }}>
                      ⚠️ Update <code>.env</code> and restart the server, then re-sync.
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={handleDetect}
            disabled={detecting || !rcStatus?.configured || isSyncRunning}
            style={{
              padding: '8px 16px',
              background: 'transparent',
              color: detecting || !rcStatus?.configured || isSyncRunning ? '#9ca3af' : '#0369a1',
              border: `1px solid ${detecting || !rcStatus?.configured || isSyncRunning ? '#e5e7eb' : '#0369a1'}`,
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: detecting || !rcStatus?.configured || isSyncRunning ? 'not-allowed' : 'pointer'
            }}
            title="Probe RevenueCat with sample users to figure out which DB field maps to app_user_id"
          >
            {detecting ? '⏳ Detecting…' : '🔍 Detect mapping'}
          </button>
          {isSyncRunning ? (
            <button
              onClick={handleStop}
              style={{
                padding: '8px 16px',
                background: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
              title="Cancel the running sync (it stops after the current customer)"
            >
              ⏹ Stop sync
            </button>
          ) : (
            <>
              <button
                onClick={() => handleSync('promo_users')}
                disabled={!rcStatus?.configured}
                style={{
                  padding: '8px 16px',
                  background: !rcStatus?.configured ? '#e5e7eb' : '#7c3aed',
                  color: !rcStatus?.configured ? '#9ca3af' : 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: !rcStatus?.configured ? 'not-allowed' : 'pointer'
                }}
                title="Sync subscriptions only for users who entered a promo code"
              >
                🔄 Sync promo users
              </button>
              <button
                onClick={() => handleSync('all')}
                disabled={!rcStatus?.configured}
                style={{
                  padding: '8px 16px',
                  background: 'transparent',
                  color: !rcStatus?.configured ? '#9ca3af' : '#7c3aed',
                  border: `1px solid ${!rcStatus?.configured ? '#e5e7eb' : '#7c3aed'}`,
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: !rcStatus?.configured ? 'not-allowed' : 'pointer'
                }}
                title="Sync subscriptions for ALL users (slow — one API call per user)"
              >
                Sync all users
              </button>
            </>
          )}
        </div>
        </div>

        {/* Revenue estimate inputs */}
        <div style={{
          marginTop: '12px',
          paddingTop: '12px',
          borderTop: '1px dashed #d1fae5',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
          fontSize: '12px',
          color: '#374151'
        }}>
          <span style={{ fontWeight: 600 }}>📊 Revenue estimate:</span>
          <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            avg promo price
            <input
              type="number"
              step="0.01"
              min="0"
              value={estimatePrice}
              onChange={(e) => setEstimatePrice(parseFloat(e.target.value) || 0)}
              style={{
                width: '70px',
                padding: '4px 6px',
                border: '1px solid #d1d5db',
                borderRadius: '4px'
              }}
            />
            USD
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            store fee + tax
            <input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={estimateDeduction}
              onChange={(e) => setEstimateDeduction(parseFloat(e.target.value) || 0)}
              style={{
                width: '60px',
                padding: '4px 6px',
                border: '1px solid #d1d5db',
                borderRadius: '4px'
              }}
            />
            %
          </label>
          <span style={{ color: '#666' }}>
            → net per subscriber: <strong>${(estimatePrice * (1 - estimateDeduction / 100)).toFixed(2)}</strong>
          </span>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-4" style={{ marginBottom: '24px' }}>
        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <div className="stat-label" style={{ color: 'rgba(255,255,255,0.8)' }}>Total Promo Codes</div>
          <div className="stat-value" style={{ color: 'white' }}>{summary.totalPromoCodes}</div>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginTop: '4px' }}>
            Unique codes used
          </div>
        </div>

        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white' }}>
          <div className="stat-label" style={{ color: 'rgba(255,255,255,0.8)' }}>Users with Promo</div>
          <div className="stat-value" style={{ color: 'white' }}>{summary.totalUsersWithPromo.toLocaleString()}</div>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginTop: '4px' }}>
            Total promo users
          </div>
        </div>

        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
          <div className="stat-label" style={{ color: 'rgba(255,255,255,0.8)' }}>Most Popular</div>
          <div className="stat-value" style={{ fontSize: '18px', color: 'white' }}>
            {summary.mostPopularPromo?.promo_code || 'N/A'}
          </div>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginTop: '4px' }}>
            {summary.mostPopularPromo?.user_count || 0} users
          </div>
        </div>

        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
          <div className="stat-label" style={{ color: 'rgba(255,255,255,0.8)' }}>Most Active</div>
          <div className="stat-value" style={{ fontSize: '18px', color: 'white' }}>
            {summary.mostActivePromo?.promo_code || 'N/A'}
          </div>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginTop: '4px' }}>
            {Math.round(summary.mostActivePromo?.median_food_logs || 0)} median logs
          </div>
        </div>
      </div>

      {/* Promo Code Trend Chart - Show at TOP for visibility */}
      {selectedPromo && (
        <div 
          className="card" 
          style={{ 
            marginBottom: '24px',
            border: '2px solid #8b5cf6',
            animation: 'fadeIn 0.3s ease'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
            <h2 className="card-title" style={{ margin: 0, color: '#1a1a1a' }}>
              📈 Usage Trends: <span style={{ 
                fontFamily: 'monospace', 
                background: '#ede9fe',
                padding: '4px 12px',
                borderRadius: '6px',
                color: '#7c3aed'
              }}>{selectedPromo}</span>
            </h2>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ fontSize: '13px', color: '#666' }}>Period:</label>
                <select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  <option value="7days">Last 7 Days</option>
                  <option value="30days">Last 30 Days</option>
                  <option value="6months">Last 6 Months</option>
                  <option value="1year">Last Year</option>
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ fontSize: '13px', color: '#666' }}>View:</label>
                <select
                  value={granularity}
                  onChange={(e) => setGranularity(e.target.value)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <button
                onClick={() => setSelectedPromo(null)}
                style={{
                  padding: '6px 12px',
                  background: '#fee2e2',
                  color: '#dc2626',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                ✕ Close
              </button>
            </div>
          </div>

          {trendLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              Loading chart data...
            </div>
          ) : trendData && trendData.trendData && trendData.trendData.length > 0 ? (
            <>
              {/* Summary Stats for this promo */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: '16px', 
                marginBottom: '24px',
                padding: '16px',
                background: '#f9fafb',
                borderRadius: '8px'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Total Users</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#8b5cf6' }}>
                    {trendData.totalStats?.total_users || 0}
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Total Food Logs</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#10b981' }}>
                    {(trendData.totalStats?.total_food_logs || 0).toLocaleString()}
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Total Calories</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#f59e0b' }}>
                    {Math.round(trendData.totalStats?.total_calories || 0).toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Charts */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                {/* New Users Chart */}
                <div>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                    👤 New User Registrations
                  </h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={trendData.trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="period" 
                        tick={{ fontSize: 11 }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip 
                        contentStyle={{ 
                          background: 'white', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                        }}
                      />
                      <Bar 
                        dataKey="newUsers" 
                        name="New Users" 
                        fill="#8b5cf6" 
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Food Logs Chart */}
                <div>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                    🍽️ Food Logs Activity
                  </h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={trendData.trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="period" 
                        tick={{ fontSize: 11 }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip 
                        contentStyle={{ 
                          background: 'white', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                        }}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="foodLogs" 
                        name="Food Logs" 
                        stroke="#10b981" 
                        strokeWidth={2}
                        dot={{ fill: '#10b981', strokeWidth: 2 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="activeUsers" 
                        name="Active Users" 
                        stroke="#3b82f6" 
                        strokeWidth={2}
                        dot={{ fill: '#3b82f6', strokeWidth: 2 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Calories Chart - Full Width */}
              <div style={{ marginTop: '24px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                  🔥 Calories Logged Over Time
                </h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={trendData.trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="period" 
                      tick={{ fontSize: 11 }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip 
                      contentStyle={{ 
                        background: 'white', 
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                      }}
                      formatter={(value) => [value.toLocaleString() + ' kcal', 'Calories']}
                    />
                    <Bar 
                      dataKey="calories" 
                      name="Calories" 
                      fill="#f59e0b" 
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              color: '#666',
              background: '#f9fafb',
              borderRadius: '8px'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>📊</div>
              <div>No activity data found for this period.</div>
              <div style={{ fontSize: '13px', marginTop: '8px' }}>
                Try selecting a different time period or granularity.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Selected Promo Code Users - Show ABOVE table */}
      {selectedPromo && topUsersByPromo[selectedPromo] && (
        <div className="card" style={{ 
          marginBottom: '24px',
          border: '2px solid #3b82f6',
          animation: 'fadeIn 0.3s ease'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 className="card-title" style={{ margin: 0 }}>
              👥 Top Users with code: <span style={{ 
                fontFamily: 'monospace', 
                background: '#dbeafe',
                padding: '4px 12px',
                borderRadius: '6px'
              }}>{selectedPromo}</span>
            </h2>
            <button
              onClick={() => setSelectedPromo(null)}
              style={{
                padding: '6px 12px',
                background: '#fee2e2',
                color: '#dc2626',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              ✕ Close
            </button>
          </div>

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>User</th>
                  <th>Email</th>
                  <th>Food Logs</th>
                  <th>Total Calories</th>
                  <th>Active Days</th>
                  <th>Registered</th>
                  <th>Profile</th>
                </tr>
              </thead>
              <tbody>
                {topUsersByPromo[selectedPromo].map((user, idx) => (
                  <tr key={user.id}>
                    <td>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '24px',
                        height: '24px',
                        background: idx === 0 ? '#fef3c7' : idx === 1 ? '#f3f4f6' : idx === 2 ? '#fed7aa' : '#f9fafb',
                        borderRadius: '50%',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {idx + 1}
                      </span>
                    </td>
                    <td>
                      <strong>{user.name || 'No name'}</strong>
                    </td>
                    <td style={{ color: '#666', fontSize: '13px' }}>
                      {user.email}
                    </td>
                    <td>
                      <strong style={{ color: '#10b981' }}>{user.food_count}</strong>
                    </td>
                    <td>{Math.round(user.total_calories).toLocaleString()} kcal</td>
                    <td>{user.active_days} days</td>
                    <td style={{ fontSize: '12px', color: '#666' }}>
                      {formatDate(user.created_at)}
                    </td>
                    <td>
                      <Link
                        to={`/users/${user.id}`}
                        style={{
                          padding: '4px 10px',
                          background: '#eff6ff',
                          color: '#3b82f6',
                          borderRadius: '4px',
                          textDecoration: 'none',
                          fontSize: '12px'
                        }}
                      >
                        View →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {topUsersByPromo[selectedPromo].length === 5 && (
            <p style={{ 
              marginTop: '12px', 
              fontSize: '13px', 
              color: '#666',
              textAlign: 'center'
            }}>
              Showing top 5 most active users. More users may exist with this promo code.
            </p>
          )}
        </div>
      )}

      {/* Key Insight */}
      {summary.mostActivePromo && summary.mostPopularPromo && (
        <div className="card" style={{ 
          background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
          border: 'none',
          marginBottom: '24px' 
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '12px', color: '#7c2d12' }}>
            💡 Key Insights
          </h2>
          <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#9a3412' }}>
            <strong>{summary.mostPopularPromo.promo_code}</strong> is the most used promo code with{' '}
            <strong>{summary.mostPopularPromo.user_count} users</strong>.
            {summary.mostActivePromo.promo_code !== summary.mostPopularPromo.promo_code && (
              <span>
                {' '}However, users with code <strong>{summary.mostActivePromo.promo_code}</strong> are most engaged with{' '}
                <strong>{Math.round(summary.mostActivePromo.median_food_logs)} median food logs</strong> per user.
              </span>
            )}
          </p>
        </div>
      )}

      {/* Sort Options */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <h2 className="card-title" style={{ margin: 0 }}>📊 All Promo Codes</h2>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="🔍 Search promo code..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                fontSize: '13px',
                width: '180px'
              }}
            />
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#666' }}>Sort by:</span>
              <select 
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '13px',
                  cursor: 'pointer'
                }}
              >
                <option value="user_count">Most Users</option>
                <option value="engagement">Highest Engagement</option>
                <option value="food_logs">Most Food Logs</option>
                <option value="recent">Most Recent</option>
                <option value="subscribers">Most Subscribers</option>
                <option value="conversion">Highest Conversion %</option>
                <option value="churn">Highest Churn %</option>
                <option value="revenue">Highest Revenue</option>
                <option value="refunds">Most Refunds</option>
                <option value="profit">Highest Profit</option>
                <option value="roi">Highest ROI %</option>
              </select>
            </div>
          </div>
        </div>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th style={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 3,
                  minWidth: '180px',
                  boxShadow: '3px 0 5px -4px rgba(0, 0, 0, 0.35)'
                }}>
                  Promo Code
                </th>
                <th title="Blogger / influencer who promoted this code">Blogger</th>
                <th title="How much you paid the blogger (any currency, converted to USD at save time)">Cost paid</th>
                <th title="Net revenue: real (proceeds from RevenueCat) or estimated using the inputs above">Revenue (net)</th>
                <th title="Net revenue − cost paid (in USD)">Profit</th>
                <th title="(revenue / cost) × 100 — 100% = you broke even, 189% means you got 1.89× your spend back">ROI %</th>
                <th title="Forecast for one next post/month based on historical average net revenue per unit">Next price forecast</th>
                <th>Users</th>
                <th title="Users who ever purchased a subscription (RevenueCat)">Subs</th>
                <th title="Currently active subscribers">Active</th>
                <th title="Users with at least one refunded subscription">Refunded</th>
                <th title="Had a subscription, no longer active (excl. refunded)">Churned</th>
                <th title="subscribers / users with this promo">Conv %</th>
                <th title="churned / subscribers">Churn %</th>
                <th title="refunded users / subscribers">Refund %</th>
                <th>Food Logs</th>
                <th>Median Logs</th>
                <th>Med. Weight</th>
                <th>Avg Daily Cal</th>
                <th>Weight Logs</th>
                <th>Activities</th>
              </tr>
            </thead>
            <tbody>
              {filteredPromoCodes.map((promo, index) => (
                <tr 
                  key={promo.promo_code}
                  style={{ 
                    background: selectedPromo === promo.promo_code ? '#f0f9ff' : 'transparent'
                  }}
                >
                  <td style={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    minWidth: '180px',
                    background: selectedPromo === promo.promo_code ? '#f0f9ff' : '#fff',
                    boxShadow: '3px 0 5px -4px rgba(0, 0, 0, 0.35)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <button
                        type="button"
                        aria-label={`${selectedPromo === promo.promo_code ? 'Collapse' : 'Expand'} details for ${promo.promo_code}`}
                        aria-expanded={selectedPromo === promo.promo_code}
                        title={selectedPromo === promo.promo_code ? 'Hide details' : 'Show details'}
                        onClick={() => setSelectedPromo(
                          selectedPromo === promo.promo_code ? null : promo.promo_code
                        )}
                        style={{
                          display: 'inline-flex',
                          flex: '0 0 auto',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '32px',
                          height: '32px',
                          padding: 0,
                          border: `1px solid ${selectedPromo === promo.promo_code ? '#3b82f6' : '#d1d5db'}`,
                          borderRadius: '7px',
                          background: selectedPromo === promo.promo_code ? '#3b82f6' : '#fff',
                          color: selectedPromo === promo.promo_code ? '#fff' : '#374151',
                          cursor: 'pointer',
                          fontSize: '18px',
                          lineHeight: 1
                        }}
                      >
                        {selectedPromo === promo.promo_code ? '▾' : '▸'}
                      </button>
                      {index === 0 && sortBy === 'user_count' && '🥇'}
                      {index === 1 && sortBy === 'user_count' && '🥈'}
                      {index === 2 && sortBy === 'user_count' && '🥉'}
                      <span style={{
                        fontWeight: '600',
                        fontFamily: 'monospace',
                        background: '#f3f4f6',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}>
                        {promo.promo_code}
                      </span>
                    </div>
                  </td>
                  {/* Blogger */}
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="text"
                      placeholder="—"
                      value={getDraft(promo, 'blogger_name')}
                      onChange={(e) => setDraft(promo.promo_code, 'blogger_name', e.target.value)}
                      onBlur={() => saveMeta(promo.promo_code)}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                      style={{
                        width: '110px',
                        padding: '4px 6px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}
                    />
                  </td>
                  {/* Cost history: every price/period is saved separately and the
                      USD totals are added together for Profit and ROI. */}
                  <td onClick={(e) => e.stopPropagation()}>
                    {(() => {
                      const inputBase = {
                        padding: '3px 5px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '4px',
                        fontSize: '12px',
                        background: 'white'
                      };
                      const periods = getCostPeriods(promo);
                      const isDirty = Array.isArray(metaDraft[promo.promo_code]?.cost_periods);
                      const saveTag = metaSaving[promo.promo_code];
                      const liveTotal = periods.reduce((sum, paymentPeriod) => {
                        const savedUsd = paymentPeriod.usd_total === null || paymentPeriod.usd_total === undefined
                          ? null
                          : Number(paymentPeriod.usd_total);
                        if (savedUsd !== null && Number.isFinite(savedUsd)) return sum + savedUsd;
                        const converted = convertToUsd(
                          Number(paymentPeriod.amount) * effectivePeriodCount(paymentPeriod),
                          paymentPeriod.currency
                        );
                        return converted === null ? sum : sum + converted;
                      }, 0);

                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '285px' }}>
                          {periods.map((paymentPeriod, periodIndex) => {
                            const count = effectivePeriodCount(paymentPeriod);
                            const hasAmount = paymentPeriod.amount !== '' && paymentPeriod.amount !== null && paymentPeriod.amount !== undefined;
                            const savedPeriodUsd = paymentPeriod.usd_total === null || paymentPeriod.usd_total === undefined
                              ? null
                              : Number(paymentPeriod.usd_total);
                            const periodUsd = savedPeriodUsd ?? convertToUsd(
                              Number(paymentPeriod.amount) * count,
                              paymentPeriod.currency
                            );
                            return (
                              <div key={periodIndex} style={{ padding: '6px', border: '1px solid #e5e7eb', borderRadius: '6px', background: '#fafafa' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                  <input
                                    aria-label={`Price for payment period ${periodIndex + 1}`}
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder="Price"
                                    value={paymentPeriod.amount}
                                    onChange={(e) => setCostPeriodDraft(promo, periodIndex, 'amount', e.target.value)}
                                    onBlur={() => saveMeta(promo.promo_code)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                    style={{ ...inputBase, width: '72px' }}
                                  />
                                  <select
                                    aria-label={`Currency for payment period ${periodIndex + 1}`}
                                    value={paymentPeriod.currency || 'USD'}
                                    onChange={(e) => setCostPeriodDraft(promo, periodIndex, 'currency', e.target.value)}
                                    onBlur={() => saveMeta(promo.promo_code)}
                                    style={{ ...inputBase, padding: '3px 2px', cursor: 'pointer' }}
                                  >
                                    {(rates?.supported || ['USD', 'EUR', 'AMD', 'RUB']).map(c => (
                                      <option key={c} value={c}>{c}</option>
                                    ))}
                                  </select>
                                  <span style={{ fontSize: '11px', color: '#9ca3af' }}>/</span>
                                  <select
                                    aria-label={`Unit for payment period ${periodIndex + 1}`}
                                    value={paymentPeriod.unit || 'post'}
                                    onChange={(e) => setCostPeriodDraft(promo, periodIndex, 'unit', e.target.value)}
                                    onBlur={() => saveMeta(promo.promo_code)}
                                    style={{ ...inputBase, padding: '3px 2px', cursor: 'pointer' }}
                                  >
                                    <option value="post">post</option>
                                    <option value="month">month</option>
                                  </select>
                                  <button
                                    type="button"
                                    aria-label={`Remove payment period ${periodIndex + 1}`}
                                    title="Remove this price period"
                                    onClick={() => removeCostPeriod(promo, periodIndex)}
                                    style={{ marginLeft: 'auto', border: 0, background: 'transparent', color: '#9ca3af', cursor: 'pointer', fontSize: '15px' }}
                                  >
                                    ×
                                  </button>
                                </div>
                                {paymentPeriod.unit === 'month' ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginTop: '4px', fontSize: '11px', color: '#6b7280' }}>
                                    <input
                                      aria-label={`Start month for payment period ${periodIndex + 1}`}
                                      type="month"
                                      value={toMonthInput(paymentPeriod.period_start)}
                                      onChange={(e) => setCostPeriodDraft(promo, periodIndex, 'period_start', fromMonthInput(e.target.value))}
                                      onBlur={() => saveMeta(promo.promo_code)}
                                      style={{ ...inputBase, padding: '3px 4px', fontSize: '11px' }}
                                    />
                                    <span>→</span>
                                    <input
                                      aria-label={`End month for payment period ${periodIndex + 1}`}
                                      type="month"
                                      value={toMonthInput(paymentPeriod.period_end)}
                                      onChange={(e) => setCostPeriodDraft(promo, periodIndex, 'period_end', fromMonthInput(e.target.value))}
                                      onBlur={() => saveMeta(promo.promo_code)}
                                      style={{ ...inputBase, padding: '3px 4px', fontSize: '11px' }}
                                    />
                                    {paymentPeriod.period_start && paymentPeriod.period_end && <span>= {count} mo</span>}
                                  </div>
                                ) : (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginTop: '4px', fontSize: '11px', color: '#6b7280' }}>
                                    <span>×</span>
                                    <input
                                      aria-label={`Post count for payment period ${periodIndex + 1}`}
                                      type="number"
                                      step="1"
                                      min="1"
                                      value={paymentPeriod.count || 1}
                                      onChange={(e) => setCostPeriodDraft(promo, periodIndex, 'count', e.target.value)}
                                      onBlur={() => saveMeta(promo.promo_code)}
                                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                      style={{ ...inputBase, width: '50px' }}
                                    />
                                    <span>posts</span>
                                  </div>
                                )}
                                {hasAmount && (
                                  <div style={{ marginTop: '4px', color: '#6b7280', fontSize: '11px' }}>
                                    {count} × {Number(paymentPeriod.amount).toLocaleString()} {paymentPeriod.currency} ={' '}
                                    <strong>${Number.isFinite(periodUsd) ? periodUsd.toFixed(2) : '?'}</strong>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          <button
                            type="button"
                            onClick={() => addCostPeriod(promo)}
                            style={{ alignSelf: 'flex-start', padding: '4px 8px', border: '1px dashed #6366f1', borderRadius: '5px', background: '#eef2ff', color: '#4f46e5', cursor: 'pointer', fontSize: '11px', fontWeight: 600 }}
                          >
                            + New price period
                          </button>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px', fontSize: '11px' }}>
                            <span style={{ color: '#374151' }}>
                              Total paid: <strong>${(isDirty ? liveTotal : Number(promo.cost_paid_usd || 0)).toFixed(2)}</strong>
                            </span>
                            {saveTag === 'saving' && <span style={{ color: '#666' }}>…</span>}
                            {saveTag === 'saved' && <span style={{ color: '#10b981' }}>✓</span>}
                            {saveTag === 'error' && <span style={{ color: '#dc2626' }}>!</span>}
                          </div>
                        </div>
                      );
                    })()}
                  </td>
                  {/* Revenue (net): real proceeds (with gross − fees breakdown) or
                      an estimate (net subs × per-sub net), shown like the cost cell. */}
                  <td>
                    {(() => {
                      const fmt = (n) => n.toLocaleString(undefined, { maximumFractionDigits: 2 });
                      const sub = { fontSize: '10px', color: '#9ca3af', whiteSpace: 'nowrap' };
                      const real = Number(promo.revenue_proceeds_usd || 0);
                      const gross = Number(promo.revenue_gross_usd || 0);
                      const netSubs = Math.max(0, (promo.subscribers || 0) - (promo.refunded_users || 0));
                      const estPerSub = estimatePrice * (1 - estimateDeduction / 100);
                      const est = Math.round(netSubs * estPerSub * 100) / 100;
                      if (real > 0) {
                        const fees = gross > real ? gross - real : 0;
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <span>
                              <strong style={{ color: '#10b981' }}>${fmt(real)}</strong>
                              <span style={{ fontSize: '10px', color: '#10b981', marginLeft: '4px' }}>real</span>
                            </span>
                            {gross > 0 && (
                              <span style={sub}>
                                gross ${fmt(gross)}{fees > 0 ? ` − $${fmt(fees)} fees` : ''}
                              </span>
                            )}
                          </div>
                        );
                      }
                      if (est > 0) {
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <span>
                              <strong style={{ color: '#6366f1' }}>${fmt(est)}</strong>
                              <span style={{ fontSize: '10px', color: '#6366f1', marginLeft: '4px' }}>est</span>
                            </span>
                            <span style={sub}>{netSubs} × ${estPerSub.toFixed(2)}</span>
                          </div>
                        );
                      }
                      return <span style={{ color: '#9ca3af' }}>-</span>;
                    })()}
                  </td>
                  {/* Profit: net revenue − cost, with the subtraction shown below. */}
                  <td>
                    {(() => {
                      const cost = Number(promo.cost_paid_usd || 0);
                      if (!(cost > 0)) return <span style={{ color: '#9ca3af' }}>-</span>;
                      const fmt = (n) => n.toLocaleString(undefined, { maximumFractionDigits: 2 });
                      const { value, source } = computeRevenueNet(promo);
                      const profit = value - cost;
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <span style={{ fontWeight: 600, color: profit >= 0 ? '#10b981' : '#ef4444' }}>
                            {profit >= 0 ? '+' : ''}${fmt(profit)}
                          </span>
                          <span style={{ fontSize: '10px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                            ${fmt(value)} <span style={{ color: '#c4c7cc' }}>{source}</span> − ${fmt(cost)}
                          </span>
                        </div>
                      );
                    })()}
                  </td>
                  {/* ROI %: revenue ÷ cost. 100% = broke even; 189% = got 1.89× back.
                      Shown as a percentage plus the ×-multiple and the division below. */}
                  <td>
                    {(() => {
                      const cost = Number(promo.cost_paid_usd || 0);
                      if (!(cost > 0)) return <span style={{ color: '#9ca3af' }}>-</span>;
                      const fmt = (n) => n.toLocaleString(undefined, { maximumFractionDigits: 2 });
                      const { value } = computeRevenueNet(promo);
                      const mult = value / cost;
                      const roi = mult * 100;
                      const color = roi >= 100 ? '#10b981' : '#f59e0b';
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <span>
                            <strong style={{ fontWeight: 600, color }}>{roi.toFixed(0)}%</strong>
                            <span style={{ fontSize: '10px', color, marginLeft: '4px' }}>{mult.toFixed(2)}×</span>
                          </span>
                          <span style={{ fontSize: '10px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                            ${fmt(value)} ÷ ${fmt(cost)}
                          </span>
                        </div>
                      );
                    })()}
                  </td>
                  {/* What-if calculator for the next post/month. Revenue per unit
                      is inferred from this promo's historical net revenue. */}
                  <td onClick={(e) => e.stopPropagation()}>
                    {(() => {
                      const periods = getCostPeriods(promo);
                      const latest = periods[periods.length - 1] || {};
                      const projection = projectionDraft[promo.promo_code] || {};
                      const amount = projection.amount !== undefined ? projection.amount : (latest.amount ?? '');
                      const currency = projection.currency || latest.currency || 'USD';
                      const unit = projection.unit || latest.unit || 'post';
                      const historicalCount = periods
                        .filter(paymentPeriod => (paymentPeriod.unit || 'post') === unit)
                        .reduce((sum, paymentPeriod) => sum + effectivePeriodCount(paymentPeriod), 0);
                      const { value: totalRevenue, source } = computeRevenueNet(promo);
                      const expectedRevenue = historicalCount > 0 ? totalRevenue / historicalCount : 0;
                      const proposedUsd = convertToUsd(amount, currency);
                      const projectedProfit = proposedUsd === null ? null : expectedRevenue - proposedUsd;
                      const projectedRoi = proposedUsd > 0 ? expectedRevenue / proposedUsd * 100 : null;
                      const nativeRate = currency === 'USD' ? 1 : rates?.rates?.[currency];
                      const maxNative = nativeRate ? expectedRevenue * nativeRate : null;
                      const color = projectedProfit === null
                        ? '#6b7280'
                        : projectedProfit >= 0 ? '#10b981' : '#ef4444';
                      const inputBase = {
                        padding: '3px 5px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '4px',
                        fontSize: '12px',
                        background: 'white'
                      };

                      if (historicalCount === 0 || expectedRevenue <= 0) {
                        return <span style={{ color: '#9ca3af', fontSize: '11px' }}>Add payment history and revenue first</span>;
                      }

                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: '225px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                            <input
                              aria-label={`Proposed price for ${promo.promo_code}`}
                              type="number"
                              min="0"
                              step="0.01"
                              value={amount}
                              onChange={(e) => setProjection(promo.promo_code, 'amount', e.target.value)}
                              style={{ ...inputBase, width: '72px' }}
                            />
                            <select
                              aria-label={`Proposed price currency for ${promo.promo_code}`}
                              value={currency}
                              onChange={(e) => setProjection(promo.promo_code, 'currency', e.target.value)}
                              style={{ ...inputBase, padding: '3px 2px', cursor: 'pointer' }}
                            >
                              {(rates?.supported || ['USD', 'EUR', 'AMD', 'RUB']).map(c => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                            <span style={{ color: '#9ca3af', fontSize: '11px' }}>/</span>
                            <select
                              aria-label={`Forecast unit for ${promo.promo_code}`}
                              value={unit}
                              onChange={(e) => setProjection(promo.promo_code, 'unit', e.target.value)}
                              style={{ ...inputBase, padding: '3px 2px', cursor: 'pointer' }}
                            >
                              <option value="post">post</option>
                              <option value="month">month</option>
                            </select>
                          </div>
                          <div style={{ fontSize: '12px', fontWeight: 600, color }}>
                            Profit: {projectedProfit === null ? '—' : `${projectedProfit >= 0 ? '+' : '−'}$${Math.abs(projectedProfit).toFixed(2)}`}
                            {projectedRoi !== null && <span style={{ marginLeft: '5px', fontSize: '10px' }}>ROI {projectedRoi.toFixed(0)}%</span>}
                          </div>
                          <div style={{ fontSize: '10px', color: '#6b7280', lineHeight: 1.35 }}>
                            Expected net: ${expectedRevenue.toFixed(2)} / {unit} ({source})
                            <br />
                            Stay profitable below: <strong>{maxNative === null ? '—' : `${maxNative.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${currency}`}</strong>
                            <br />
                            Based on {historicalCount} historical {unit}{historicalCount === 1 ? '' : 's'}
                          </div>
                        </div>
                      );
                    })()}
                  </td>
                  {/* Users */}
                  <td>
                    <strong style={{ color: '#3b82f6' }}>{promo.user_count}</strong>
                  </td>
                  {/* Subs */}
                  <td>
                    <strong style={{ color: '#7c3aed' }}>{promo.subscribers || 0}</strong>
                  </td>
                  {/* Active */}
                  <td>
                    <span style={{ color: '#10b981', fontWeight: 500 }}>
                      {promo.active_subs || 0}
                      {promo.trial_users > 0 && (
                        <span style={{ fontSize: '11px', color: '#6b7280', marginLeft: '4px' }}>
                          ({promo.trial_users} trial)
                        </span>
                      )}
                    </span>
                  </td>
                  {/* Refunded */}
                  <td>
                    <span style={{ color: (promo.refunded_users || 0) > 0 ? '#dc2626' : '#9ca3af' }}>
                      {promo.refunded_users || 0}
                      {(promo.refunds || 0) > (promo.refunded_users || 0) && (
                        <span style={{ fontSize: '11px', color: '#6b7280', marginLeft: '4px' }}>
                          ({promo.refunds} subs)
                        </span>
                      )}
                    </span>
                  </td>
                  {/* Churned */}
                  <td>
                    <span style={{ color: (promo.churned || 0) > 0 ? '#ef4444' : '#9ca3af' }}>
                      {promo.churned || 0}
                    </span>
                  </td>
                  {/* Conv % */}
                  <td>
                    {promo.user_count > 0 ? (
                      <span style={{
                        fontWeight: 600,
                        color: (promo.conversion_rate || 0) >= 30 ? '#10b981'
                          : (promo.conversion_rate || 0) >= 10 ? '#f59e0b'
                          : '#9ca3af'
                      }}>
                        {(promo.conversion_rate || 0).toFixed(1)}%
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>-</span>
                    )}
                  </td>
                  {/* Churn % */}
                  <td>
                    {(promo.subscribers || 0) > 0 ? (
                      <span style={{
                        fontWeight: 600,
                        color: (promo.churn_rate || 0) >= 50 ? '#ef4444'
                          : (promo.churn_rate || 0) >= 20 ? '#f59e0b'
                          : '#10b981'
                      }}>
                        {(promo.churn_rate || 0).toFixed(1)}%
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>-</span>
                    )}
                  </td>
                  {/* Refund % */}
                  <td>
                    {(promo.subscribers || 0) > 0 ? (
                      <span style={{
                        fontWeight: 600,
                        color: (promo.refund_rate || 0) >= 20 ? '#dc2626'
                          : (promo.refund_rate || 0) >= 5 ? '#f59e0b'
                          : '#9ca3af'
                      }}>
                        {(promo.refund_rate || 0).toFixed(1)}%
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>-</span>
                    )}
                  </td>
                  {/* Food Logs */}
                  <td>{promo.total_food_logs.toLocaleString()}</td>
                  {/* Median Logs */}
                  <td>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <span style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: getEngagementColor(promo.median_food_logs)
                      }} />
                      <strong>{Math.round(promo.median_food_logs)}</strong>
                    </span>
                  </td>
                  {/* Med. Weight */}
                  <td>
                    {promo.median_weight && promo.median_weight > 0 ? (
                      <span style={{ fontWeight: '500', color: '#6366f1' }}>
                        {Number(promo.median_weight).toFixed(1)} kg
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>-</span>
                    )}
                  </td>
                  {/* Avg Daily Cal */}
                  <td>
                    {promo.avg_daily_calories && promo.avg_daily_calories > 0 ? (
                      <span style={{ fontWeight: '500', color: '#f59e0b' }}>
                        {Number(promo.avg_daily_calories).toLocaleString()}
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>-</span>
                    )}
                  </td>
                  {/* Weight Logs */}
                  <td>{promo.weight_logs}</td>
                  {/* Activities */}
                  <td>{promo.activity_logs}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Engagement Overview */}
      <div style={{ marginTop: '24px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '16px' }}>
          📈 Engagement by Promo Code
        </h2>
        <div className="grid grid-3">
          {filteredPromoCodes
            .filter(p => p.user_count >= 1)
            .slice(0, 6)
            .map(promo => {
              const engagementScore = Math.min(100, Math.round((promo.median_food_logs / 50) * 100));
              return (
                <div 
                  key={promo.promo_code} 
                  className="card"
                  style={{ 
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    border: selectedPromo === promo.promo_code ? '2px solid #3b82f6' : '1px solid #e5e7eb'
                  }}
                  onClick={() => setSelectedPromo(
                    selectedPromo === promo.promo_code ? null : promo.promo_code
                  )}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <h3 style={{ 
                      fontSize: '16px', 
                      fontWeight: '600', 
                      fontFamily: 'monospace',
                      margin: 0
                    }}>
                      {promo.promo_code}
                    </h3>
                    <span style={{
                      padding: '2px 8px',
                      background: getEngagementColor(promo.median_food_logs),
                      color: 'white',
                      borderRadius: '10px',
                      fontSize: '11px',
                      fontWeight: '600'
                    }}>
                      {engagementScore}%
                    </span>
                  </div>

                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr',
                    gap: '12px',
                    marginTop: '16px'
                  }}>
                    <div>
                      <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Users</div>
                      <div style={{ fontSize: '20px', fontWeight: '700', color: '#3b82f6' }}>
                        {promo.user_count}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Median Logs</div>
                      <div style={{ fontSize: '20px', fontWeight: '700', color: '#10b981' }}>
                        {Math.round(promo.median_food_logs)}
                      </div>
                    </div>
                  </div>

                  <div style={{
                    marginTop: '12px',
                    padding: '8px',
                    background: '#f9fafb',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: '#666'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Food: {promo.total_food_logs}</span>
                      <span>Weight: {promo.weight_logs}</span>
                      <span>Activity: {promo.activity_logs}</span>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* Legend */}
      <div className="card" style={{ marginTop: '24px', background: '#f9fafb' }}>
        <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>
          📋 Engagement Score Legend
        </h3>
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#10b981' }} />
            <span style={{ fontSize: '13px' }}>Excellent (50+ median logs)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#3b82f6' }} />
            <span style={{ fontSize: '13px' }}>Good (20-49 median logs)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#f59e0b' }} />
            <span style={{ fontSize: '13px' }}>Fair (5-19 median logs)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#ef4444' }} />
            <span style={{ fontSize: '13px' }}>Low (&lt;5 median logs)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromoCodes;
